import { bus } from '@/core/EventBus';

const MASTER_VOLUME = 0.42;
const MIN_INTERVAL = {
  step: 0.075,
  impact: 0.025,
  skid: 0.22,
} as const;

/**
 * Doorlopende stemmen, in tegenstelling tot de eenmalige geluiden hieronder.
 *
 * Rijden was volledig stil: er was geen motor, geen band, geen omgevingsgeluid. Dat las als
 * een kapotte build, niet als sfeer. Deze twee stemmen draaien continu en worden alleen in
 * volume en toonhoogte gestuurd — starten en stoppen van oscillatoren per frame zou klikken
 * geven.
 */
const ENGINE = {
  /** Toonhoogte bij stilstand en op topsnelheid. Laag: een sedan uit 1978, geen sportwagen. */
  idleHz: 34,
  maxHz: 132,
  idleGain: 0.05,
  maxGain: 0.17,
  /** Hoe snel toon en volume de snelheid volgen. Te snel klinkt als een naaimachine. */
  glideSeconds: 0.09,
  /** Topsnelheid waartegen we schalen; hoort bij `VEHICLE.maxSpeed`. */
  referenceSpeed: 9.2,
} as const;

const AMBIENCE = {
  gain: 0.035,
  /** Zware, verre stadsruis. Hoger en het gaat sissen. */
  cutoffHz: 210,
} as const;

export class Sfx {
  readonly #context: AudioContext | null;
  readonly #master: GainNode | null;
  readonly #listeners: Array<() => void> = [];
  #muted = false;
  #lastStep = 0;
  #lastImpact = 0;
  #lastSkid = 0;
  #engine: { oscillator: OscillatorNode; filter: BiquadFilterNode; gain: GainNode } | null = null;
  #ambience: GainNode | null = null;
  #driving = false;

  constructor() {
    const audioWindow = globalThis.window;
    const AudioContextCtor = audioWindow?.AudioContext ?? audioWindow?.webkitAudioContext;
    if (!AudioContextCtor) {
      this.#context = null;
      this.#master = null;
      return;
    }

    this.#context = new AudioContextCtor();
    this.#master = this.#context.createGain();
    this.#master.gain.value = MASTER_VOLUME;
    this.#master.connect(this.#context.destination);
    this.#startContinuousVoices();
    this.#bindUserGestureUnlock();
    this.#subscribe();
  }

  get muted(): boolean {
    return this.#muted;
  }

  set muted(value: boolean) {
    this.#muted = value;
    if (!this.#master || !this.#context) return;
    this.#master.gain.setTargetAtTime(value ? 0 : MASTER_VOLUME, this.#context.currentTime, 0.015);
    // Het omgevingsbed loopt continu door; zonder dit blijft het bij mute zachtjes staan.
    this.#ambience?.gain.setTargetAtTime(value ? 0 : AMBIENCE.gain, this.#context.currentTime, 0.05);
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    return this.#muted;
  }

  dispose(): void {
    for (const off of this.#listeners) off();
    this.#listeners.length = 0;
    void this.#context?.close();
  }

  #subscribe(): void {
    this.#listeners.push(
      bus.on('combat:shot', (event) => {
        void event;
        this.#gunshot();
      }),
      bus.on('combat:impact', (event) => this.#impact(event.outcome)),
      bus.on('weapon:reload', () => this.#reload()),
      bus.on('player:damaged', () => this.#damage()),
      bus.on('player:step', (event) => this.#step(event.speed)),
      bus.on('mission:completed', () => this.#uiGood()),
      bus.on('mission:failed', () => this.#uiBad()),
      bus.on('soldier:rescued', () => this.#uiGood()),
      bus.on('enemy:alerted', () => this.#alert()),
      bus.on('vehicle:entered', () => { this.#driving = true; }),
      bus.on('vehicle:exited', () => { this.#driving = false; }),
      bus.on('vehicle:burned', () => { this.#driving = false; }),
      bus.on('vehicle:skid', (event) => this.#skid(event.severity)),
      bus.on('vehicle:damaged', (event) => {
        if (event.reason === 'collision') this.#crash(event.speed);
      }),
    );
  }

  /**
   * Per simulatiestap aanroepen met de snelheid van de bestuurde auto.
   *
   * `Sfx` was tot nu toe puur event-gedreven, maar een motor is geen gebeurtenis: hij moet de
   * snelheid volgen zolang je rijdt.
   */
  update(drivingSpeed: number): void {
    const context = this.#context;
    const engine = this.#engine;
    if (!context || !engine) return;

    const speed = this.#driving ? Math.min(1, Math.abs(drivingSpeed) / ENGINE.referenceSpeed) : 0;
    const now = context.currentTime;
    const target = this.#driving ? speed : 0;

    engine.oscillator.frequency.setTargetAtTime(
      ENGINE.idleHz + (ENGINE.maxHz - ENGINE.idleHz) * speed,
      now,
      ENGINE.glideSeconds,
    );
    engine.gain.gain.setTargetAtTime(
      this.#driving ? ENGINE.idleGain + (ENGINE.maxGain - ENGINE.idleGain) * target : 0,
      now,
      ENGINE.glideSeconds,
    );
    engine.filter.frequency.setTargetAtTime(320 + 900 * speed, now, ENGINE.glideSeconds);
  }

  /**
   * Motor en omgevingsbed draaien continu en staan standaard op nul volume. Ze aan- en
   * uitzetten per frame zou klikken geven; een oscillator die stil doordraait kost niets.
   */
  #startContinuousVoices(): void {
    const context = this.#context;
    const master = this.#master;
    if (!context || !master) return;

    const oscillator = context.createOscillator();
    oscillator.type = 'sawtooth';
    oscillator.frequency.value = ENGINE.idleHz;
    const filter = context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 320;
    const gain = context.createGain();
    gain.gain.value = 0;
    oscillator.connect(filter).connect(gain).connect(master);
    oscillator.start();
    this.#engine = { oscillator, filter, gain };

    const bed = this.#noise(2, true);
    const bedFilter = context.createBiquadFilter();
    bedFilter.type = 'lowpass';
    bedFilter.frequency.value = AMBIENCE.cutoffHz;
    const bedGain = context.createGain();
    bedGain.gain.value = AMBIENCE.gain;
    bed.connect(bedFilter).connect(bedGain).connect(master);
    bed.start();
    this.#ambience = bedGain;
  }

  #skid(severity: number): void {
    const context = this.#context;
    const master = this.#master;
    if (!context || !master || context.currentTime - this.#lastSkid < MIN_INTERVAL.skid) return;
    this.#lastSkid = context.currentTime;

    const now = context.currentTime;
    const noise = this.#noise(0.36);
    const filter = context.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 1400 + severity * 900;
    filter.Q.value = 7;
    const gain = context.createGain();
    const peak = 0.12 + severity * 0.2;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(peak, now + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.34);
    noise.connect(filter).connect(gain).connect(master);
    noise.start(now);
    noise.stop(now + 0.36);
  }

  #crash(speed: number): void {
    const context = this.#context;
    const master = this.#master;
    if (!context || !master) return;

    const now = context.currentTime;
    const weight = Math.min(1, speed / 9.2);
    const noise = this.#noise(0.22);
    const filter = context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 260 + weight * 420;
    const gain = context.createGain();
    gain.gain.setValueAtTime(0.28 + weight * 0.4, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
    noise.connect(filter).connect(gain).connect(master);
    noise.start(now);
    noise.stop(now + 0.22);

    this.#tone(72, 0.16, 0.24 + weight * 0.22, 'square', now);
  }

  #bindUserGestureUnlock(): void {
    const audioWindow = globalThis.window;
    if (!audioWindow) return;
    const unlock = (): void => {
      void this.#context?.resume();
      audioWindow.removeEventListener('pointerdown', unlock);
      audioWindow.removeEventListener('keydown', unlock);
    };
    audioWindow.addEventListener('pointerdown', unlock);
    audioWindow.addEventListener('keydown', unlock);
  }

  #gunshot(): void {
    const context = this.#context;
    const master = this.#master;
    if (!context || !master) return;

    const now = context.currentTime;
    const noise = this.#noise(0.06);
    const crack = context.createBiquadFilter();
    crack.type = 'highpass';
    crack.frequency.value = 900;
    const gain = context.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(1.2, now + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.075);
    noise.connect(crack).connect(gain).connect(master);
    noise.start(now);
    noise.stop(now + 0.08);

    this.#tone(105, 0.055, 0.18, 'sawtooth', now);
  }

  #impact(outcome: 'target' | 'wall'): void {
    const context = this.#context;
    const master = this.#master;
    if (!context || !master || context.currentTime - this.#lastImpact < MIN_INTERVAL.impact) return;
    this.#lastImpact = context.currentTime;

    const now = context.currentTime;
    const noise = this.#noise(outcome === 'wall' ? 0.045 : 0.035);
    const filter = context.createBiquadFilter();
    filter.type = outcome === 'wall' ? 'bandpass' : 'lowpass';
    filter.frequency.value = outcome === 'wall' ? 1800 : 520;
    const gain = context.createGain();
    gain.gain.setValueAtTime(outcome === 'wall' ? 0.34 : 0.22, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);
    noise.connect(filter).connect(gain).connect(master);
    noise.start(now);
    noise.stop(now + 0.055);
  }

  #reload(): void {
    const context = this.#context;
    if (!context) return;
    const now = context.currentTime;
    this.#tone(520, 0.045, 0.12, 'square', now);
    this.#tone(310, 0.07, 0.1, 'triangle', now + 0.14);
  }

  #damage(): void {
    this.#tone(150, 0.14, 0.2, 'sawtooth');
  }

  #step(speed: number): void {
    const context = this.#context;
    const master = this.#master;
    if (!context || !master || context.currentTime - this.#lastStep < MIN_INTERVAL.step) return;
    this.#lastStep = context.currentTime;

    const now = context.currentTime;
    const noise = this.#noise(0.045);
    const filter = context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = speed > 4 ? 380 : 300;
    const gain = context.createGain();
    gain.gain.setValueAtTime(speed > 4 ? 0.18 : 0.11, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.055);
    noise.connect(filter).connect(gain).connect(master);
    noise.start(now);
    noise.stop(now + 0.06);
  }

  #alert(): void {
    const now = this.#context?.currentTime ?? 0;
    this.#tone(880, 0.08, 0.08, 'square', now);
    this.#tone(660, 0.1, 0.08, 'square', now + 0.09);
  }

  #uiGood(): void {
    const now = this.#context?.currentTime ?? 0;
    this.#tone(440, 0.05, 0.08, 'triangle', now);
    this.#tone(660, 0.08, 0.08, 'triangle', now + 0.055);
  }

  #uiBad(): void {
    const now = this.#context?.currentTime ?? 0;
    this.#tone(220, 0.08, 0.1, 'sawtooth', now);
    this.#tone(165, 0.12, 0.09, 'sawtooth', now + 0.08);
  }

  #tone(
    frequency: number,
    duration: number,
    volume: number,
    type: OscillatorType,
    startAt = this.#context?.currentTime ?? 0,
  ): void {
    const context = this.#context;
    const master = this.#master;
    if (!context || !master) return;

    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, startAt);
    gain.gain.setValueAtTime(0.0001, startAt);
    gain.gain.exponentialRampToValueAtTime(volume, startAt + 0.006);
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);
    oscillator.connect(gain).connect(master);
    oscillator.start(startAt);
    oscillator.stop(startAt + duration + 0.01);
  }

  /** @param loop Voor doorlopende stemmen zoals het omgevingsbed; eenmalige geluiden niet. */
  #noise(duration: number, loop = false): AudioBufferSourceNode {
    const context = this.#context;
    if (!context) throw new Error('AudioContext ontbreekt');

    const length = Math.max(1, Math.floor(context.sampleRate * duration));
    const buffer = context.createBuffer(1, length, context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const source = context.createBufferSource();
    source.buffer = buffer;
    source.loop = loop;
    return source;
  }
}

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}
